import React, { useState, useEffect } from "react";
import { getWithdrawals, createWithdrawal, updateWithdrawal } from "../../firebase/firestoreService";
import { MOBILE_OPERATORS, generateChargeId, requestPayout } from "../../services/paychangu";
import { useAuth } from "../../context/AuthContext";

const fmt = (n) => `MK ${Number(n || 0).toLocaleString()}`;

const fmtDate = (ts) => {
  if (!ts) return "—";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) +
    " · " + d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
};

const STATUS_STYLE = {
  Pending:    { background: "#fff3e0", color: "#f57c00" },
  Processing: { background: "#e3f2fd", color: "#1565c0" },
  Success:    { background: "#e8f5e9", color: "#2e7d32" },
  Failed:     { background: "#fde8e8", color: "#c0392b" },
};

export default function WithdrawalsSection() {
  const { user } = useAuth();
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [formOpen,    setFormOpen]    = useState(false);
  const [form,        setForm]        = useState({ operator: "airtel", phone: "", amount: "" });
  const [submitting,  setSubmitting]  = useState(false);
  const [error,       setError]       = useState("");

  const load = async () => {
    setLoading(true);
    setWithdrawals(await getWithdrawals());
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    const amount = parseFloat(form.amount);
    const phone  = form.phone.trim();
    if (!phone)                      { setError("Enter a mobile money number."); return; }
    if (isNaN(amount) || amount <= 0) { setError("Enter a valid amount."); return; }

    setSubmitting(true);
    setError("");

    const chargeId = generateChargeId();
    const withdrawalId = await createWithdrawal({
      operator:      form.operator,
      phone,
      amount,
      chargeId,
      status:        "Pending",
      requestedBy:   user?.email || "",
    });

    const result = await requestPayout({ operator: form.operator, phone, amount, chargeId });

    if (result.success) {
      await updateWithdrawal(withdrawalId, { status: "Processing" });
    } else {
      await updateWithdrawal(withdrawalId, { status: "Failed", failureReason: result.error });
      setError(result.error);
    }

    setForm({ operator: "airtel", phone: "", amount: "" });
    setFormOpen(false);
    await load();
    setSubmitting(false);
  };

  const totalWithdrawn = withdrawals
    .filter((w) => w.status === "Success")
    .reduce((s, w) => s + (w.amount || 0), 0);
  const totalPending = withdrawals
    .filter((w) => w.status === "Pending" || w.status === "Processing")
    .reduce((s, w) => s + (w.amount || 0), 0);

  return (
    <div>
      <div className="dash-main__header">
        <h2 className="dash-main__title">Withdrawals</h2>
        <button className="btn-primary" onClick={() => { setFormOpen((v) => !v); setError(""); }}>
          {formOpen ? "Cancel" : "+ New Withdrawal"}
        </button>
      </div>

      <div className="stat-cards">
        <div className="stat-card">
          <div className="stat-card__label">Total Withdrawn</div>
          <div className="stat-card__value" style={{ fontSize: 20 }}>{fmt(totalWithdrawn)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card__label">Pending / Processing</div>
          <div className="stat-card__value" style={{ fontSize: 20 }}>{fmt(totalPending)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card__label">Total Requests</div>
          <div className="stat-card__value">{withdrawals.length}</div>
        </div>
      </div>

      {formOpen && (
        <div className="referrals__form-card">
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Operator</label>
              <select className="form-input" value={form.operator} onChange={(e) => setF("operator", e.target.value)}>
                {MOBILE_OPERATORS.map((op) => (
                  <option key={op.id} value={op.id}>{op.label}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Mobile Money Number</label>
              <input className="form-input" placeholder="e.g. 0990000000"
                value={form.phone} onChange={(e) => setF("phone", e.target.value)} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Amount (MWK)</label>
              <input className="form-input" type="number" min="1" placeholder="1000"
                value={form.amount} onChange={(e) => setF("amount", e.target.value)} />
            </div>
          </div>
          {error && <p style={{ fontSize: 12, color: "#c0392b", marginTop: 6 }}>{error}</p>}
          <div style={{ marginTop: 14, display: "flex", justifyContent: "flex-end" }}>
            <button className="btn-primary" onClick={handleSubmit} disabled={submitting}>
              {submitting ? "Submitting…" : "Request Withdrawal"}
            </button>
          </div>
        </div>
      )}

      <div className="dash-table-wrap">
        <table className="dash-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Operator</th>
              <th>Number</th>
              <th>Amount</th>
              <th>Requested By</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} style={{ textAlign: "center", color: "#aaa", padding: 32, fontSize: 13 }}>
                  Loading…
                </td>
              </tr>
            ) : withdrawals.length === 0 ? (
              <tr>
                <td colSpan={6} className="dash-empty">No withdrawals yet.</td>
              </tr>
            ) : withdrawals.map((w) => (
              <tr key={w.id}>
                <td>{fmtDate(w.createdAt)}</td>
                <td>{MOBILE_OPERATORS.find((o) => o.id === w.operator)?.label || w.operator}</td>
                <td>{w.phone}</td>
                <td style={{ fontWeight: 600 }}>{fmt(w.amount)}</td>
                <td>{w.requestedBy || "—"}</td>
                <td>
                  <span style={{
                    ...(STATUS_STYLE[w.status] || STATUS_STYLE.Pending),
                    fontSize: 10, fontWeight: 700, letterSpacing: 1, padding: "3px 8px", borderRadius: 20,
                  }}>
                    {w.status || "Pending"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
